import { Container, Graphics, Text } from 'pixi.js';
import { draw3DButton, drawBevelPanel, GUGI_SHADOW } from '../rendering/PanelUtils';
import type { GameState, GameStateManager } from './GameStateManager';
import { BaseState } from './BaseState';
import { scoreBand, type ScoredItem } from '../upgrades/Quality';
import type { UpgradeDef } from '../upgrades/UpgradeData';
import type { WeaponDef } from '../upgrades/WeaponData';

// ─── Accent palette (matching other screens) ─────────────
const ACCENT      = 0xDD2233;
const ACCENT_MED  = 0xBB1122;
const ACCENT_DIM  = 0x881122;

export type RunEndCause = 'structure' | 'energy';

export interface EndRunData {
  cause: RunEndCause;
  xpGained: number;
  killCount: number;
  lootBag: { def: UpgradeDef | WeaponDef; item: ScoredItem }[];
  coinsEarned: number;
}

export class EndRunState implements GameState {
  private container = new Container();
  private manager: GameStateManager;
  private data: EndRunData;

  constructor(manager: GameStateManager, data: EndRunData) {
    this.manager = manager;
    this.data = data;
  }

  enter() {
    // Transfer XP, money, and loot to persistent systems before showing results
    if (this.data.xpGained > 0) {
      this.manager.stats.addXp(this.data.xpGained);
    }
    this.manager.money += this.data.coinsEarned;
    for (const loot of this.data.lootBag) {
      if (loot.def.kind === 'weapon') {
        this.manager.upgrades.addWeaponToInventory(loot.item);
      } else {
        this.manager.upgrades.addToInventory(loot.item);
      }
    }

    const app = this.manager.app;
    app.stage.addChild(this.container);

    // Background
    const bg = new Graphics();
    bg.rect(0, 0, app.screen.width, app.screen.height);
    bg.fill(0x080a10);
    this.container.addChild(bg);

    // Two-level grid overlay — minor (40px) + major (80px)
    const grid = new Graphics();
    const W = app.screen.width, H = app.screen.height;
    for (let x = 0; x < W; x += 40) { grid.moveTo(x, 0); grid.lineTo(x, H); }
    for (let y = 0; y < H; y += 40) { grid.moveTo(0, y); grid.lineTo(W, y); }
    grid.stroke({ color: 0x111822, width: 1 });
    for (let x = 0; x < W; x += 80) { grid.moveTo(x, 0); grid.lineTo(x, H); }
    for (let y = 0; y < H; y += 80) { grid.moveTo(0, y); grid.lineTo(W, y); }
    grid.stroke({ color: 0x16202e, width: 1 });
    this.container.addChild(grid);

    // Top accent line
    const topLine = new Graphics();
    topLine.moveTo(0, 0); topLine.lineTo(app.screen.width, 0);
    topLine.stroke({ color: ACCENT, width: 2 });
    this.container.addChild(topLine);

    this.buildPanel();
  }

  private buildPanel() {
    const app = this.manager.app;
    const cx = app.screen.width / 2;
    const cy = app.screen.height / 2;

    const { cause, xpGained, killCount, lootBag, coinsEarned } = this.data;

    // Panel dimensions — cap to viewport so portrait phones don't clip.
    const PANEL_W = Math.min(500, app.screen.width - 20);
    const lootRowH = 28;
    const MAX_PANEL_H = app.screen.height - 40; // 20px margin top+bottom
    const basePanelH = 320; // height without loot
    const lootHeaderH = lootBag.length > 0 ? 24 : 28; // header + spacing
    const totalLootH = lootBag.length > 0 ? lootBag.length * lootRowH : 0;
    const uncappedH = basePanelH + lootHeaderH + totalLootH;
    const PANEL_H = Math.min(uncappedH, MAX_PANEL_H);
    // How much space is available for the loot list inside the panel
    const availableLootH = PANEL_H - basePanelH - lootHeaderH;
    const lootNeedsScroll = totalLootH > availableLootH && lootBag.length > 0;
    const px = cx - PANEL_W / 2;
    const py = cy - PANEL_H / 2;

    // Panel background
    const panel = new Graphics();
    drawBevelPanel(panel, px, py, PANEL_W, PANEL_H, { fill: 0x0d1018, border: ACCENT_DIM, radius: 8, rivets: true });
    this.container.addChild(panel);

    // Top accent strip
    const strip = new Graphics();
    strip.roundRect(px, py, PANEL_W, 6, 8);
    strip.fill(ACCENT);
    this.container.addChild(strip);

    let y = py + 36;

    // Mission terminated header
    const header = new Text({
      text: 'MISSION TERMINATED',
      style: { fontFamily: 'Gugi', fontSize: 26, fill: 0xffffff, fontWeight: 'bold', dropShadow: GUGI_SHADOW },
    });
    header.anchor.set(0.5, 0);
    header.position.set(cx, y);
    this.container.addChild(header);
    y += header.height + 12;

    // Cause badge
    const causeLabel = cause === 'structure' ? 'STRUCTURAL FAILURE' : 'POWER FAILURE';
    const causeColor = cause === 'structure' ? 0xff4444 : 0xffaa22;

    const badgeW = 240, badgeH = 34;
    const badge = new Graphics();
    badge.roundRect(cx - badgeW / 2, y, badgeW, badgeH, 5);
    badge.fill(cause === 'structure' ? 0x2a0808 : 0x2a1800);
    badge.roundRect(cx - badgeW / 2, y, badgeW, badgeH, 5);
    badge.stroke({ color: causeColor, width: 1 });
    this.container.addChild(badge);

    const causeText = new Text({
      text: causeLabel,
      style: { fontFamily: 'Offside', fontSize: 15, fill: causeColor, fontWeight: 'bold' },
    });
    causeText.anchor.set(0.5, 0.5);
    causeText.position.set(cx, y + badgeH / 2);
    this.container.addChild(causeText);
    y += badgeH + 32;

    // Divider
    const div1 = new Graphics();
    div1.moveTo(px + 24, y); div1.lineTo(px + PANEL_W - 24, y);
    div1.stroke({ color: ACCENT_DIM, width: 1 });
    this.container.addChild(div1);
    y += 20;

    // Stats row
    const stats: { label: string; value: string; color: number }[] = [
      { label: 'XP GAINED',  value: `+${xpGained}`,     color: 0x44ff88 },
      { label: 'ENEMIES',    value: `${killCount}`,      color: 0xaaaaaa },
      { label: 'ITEMS',      value: `${lootBag.length}`, color: 0x4488ff },
      { label: 'CREDITS',    value: `+${coinsEarned}`,   color: 0xffdd44 },
    ];

    const colW = PANEL_W / stats.length;
    for (let i = 0; i < stats.length; i++) {
      const { label, value, color } = stats[i]!;
      const statX = px + colW * i + colW / 2;

      const valText = new Text({
        text: value,
        style: { fontFamily: 'Offside', fontSize: 28, fill: color, fontWeight: 'bold' },
      });
      valText.anchor.set(0.5, 0);
      valText.position.set(statX, y);
      this.container.addChild(valText);

      const lblText = new Text({
        text: label,
        style: { fontFamily: 'Offside', fontSize: 11, fill: 0x556677 },
      });
      lblText.anchor.set(0.5, 0);
      lblText.position.set(statX, y + valText.height + 4);
      this.container.addChild(lblText);
    }

    y += 60;

    // Divider
    const div2 = new Graphics();
    div2.moveTo(px + 24, y); div2.lineTo(px + PANEL_W - 24, y);
    div2.stroke({ color: ACCENT_DIM, width: 1 });
    this.container.addChild(div2);
    y += 16;

    // Loot section
    if (lootBag.length > 0) {
      const lootHeader = new Text({
        text: 'ITEMS RECOVERED',
        style: { fontFamily: 'Gugi', fontSize: 12, fill: 0x556677, dropShadow: GUGI_SHADOW },
      });
      lootHeader.anchor.set(0.5, 0);
      lootHeader.position.set(cx, y);
      this.container.addChild(lootHeader);
      y += 20;

      // Scrollable loot list
      const lootContainer = new Container();
      let lootY = 0;
      for (const entry of lootBag) {
        const band = scoreBand(entry.item.score);
        const itemText = new Text({
          text: `[${entry.item.score}]  ${entry.item.name}`,
          style: { fontFamily: 'Offside', fontSize: 13, fill: band.color },
        });
        itemText.anchor.set(0.5, 0);
        itemText.position.set(cx - px - 24, lootY); // relative to loot container
        lootContainer.addChild(itemText);
        lootY += lootRowH;
      }

      const visibleH = lootNeedsScroll ? availableLootH : totalLootH;

      // Wrapper positioned in the panel
      const lootWrapper = new Container();
      lootWrapper.position.set(px + 24, y);
      lootWrapper.addChild(lootContainer);

      if (lootNeedsScroll) {
        // Mask to clip the list
        const maskGfx = new Graphics();
        maskGfx.rect(0, 0, PANEL_W - 48, visibleH);
        maskGfx.fill(0xffffff);
        lootWrapper.addChild(maskGfx);
        lootWrapper.mask = maskGfx;

        // Scroll bar track + thumb
        const trackX = PANEL_W - 48 - 6;
        const thumbRatio = visibleH / totalLootH;
        const thumbH = Math.max(20, visibleH * thumbRatio);

        const scrollTrack = new Graphics();
        scrollTrack.roundRect(trackX, 0, 4, visibleH, 2);
        scrollTrack.fill(0x1a2030);
        lootWrapper.addChild(scrollTrack);

        const scrollThumb = new Graphics();
        scrollThumb.roundRect(trackX, 0, 4, thumbH, 2);
        scrollThumb.fill(ACCENT_DIM);
        lootWrapper.addChild(scrollThumb);

        const maxScroll = totalLootH - visibleH;

        const updateScroll = () => {
          const scrollY = -lootContainer.y;
          const ratio = scrollY / maxScroll;
          scrollThumb.y = ratio * (visibleH - thumbH);
        };

        // Mouse wheel scrolling on the whole panel area
        lootWrapper.eventMode = 'static';
        lootWrapper.hitArea = { contains: (x: number, y: number) => x >= 0 && x <= PANEL_W - 48 && y >= 0 && y <= visibleH };
        lootWrapper.on('wheel', (e: WheelEvent) => {
          lootContainer.y = Math.max(-(maxScroll), Math.min(0, lootContainer.y - e.deltaY));
          updateScroll();
        });
      }

      this.container.addChild(lootWrapper);
      y += visibleH + 8;
    } else {
      const noLoot = new Text({
        text: 'NO ITEMS RECOVERED',
        style: { fontFamily: 'Gugi', fontSize: 13, fill: 0x334455, dropShadow: GUGI_SHADOW },
      });
      noLoot.anchor.set(0.5, 0);
      noLoot.position.set(cx, y);
      this.container.addChild(noLoot);
      y += 28;
    }

    // Divider above button
    const div3 = new Graphics();
    div3.moveTo(px + 24, y); div3.lineTo(px + PANEL_W - 24, y);
    div3.stroke({ color: ACCENT_DIM, width: 1 });
    this.container.addChild(div3);
    y += 20;

    // Return to base button
    const btnW = 220, btnH = 40;
    const btn = new Container();
    btn.position.set(cx - btnW / 2, y);

    const btnBg = new Graphics();
    draw3DButton(btnBg, btnW, btnH, ACCENT_MED);
    btn.addChild(btnBg);

    const btnLabel = new Text({
      text: 'RETURN TO BASE',
      style: { fontFamily: 'Gugi', fontSize: 17, fill: 0xffffff, dropShadow: GUGI_SHADOW },
    });
    btnLabel.anchor.set(0.5, 0.5);
    btnLabel.position.set(btnW / 2, btnH / 2);
    btn.addChild(btnLabel);

    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    btn.on('pointerover', () => { btnBg.tint = 0xdddddd; });
    btn.on('pointerout',  () => { btnBg.tint = 0xffffff; });
    btn.on('pointerdown', () => this.manager.switchTo(new BaseState(this.manager)));
    this.container.addChild(btn);
  }

  exit() {
    this.container.removeFromParent();
    this.container.destroy({ children: true });
  }

   
  update(_dt: number) {
    // Static screen — no per-frame updates needed
  }
}
